<script lang="ts">
  import type { Snippet } from "svelte";

  interface Props {
    input?: Snippet;
    children?: Snippet;
    variantGray?: boolean;
  }

  let { input = defaultInput, variantGray = false, children }: Props = $props();
</script>

{#snippet defaultInput()}
  <input type="checkbox" />
{/snippet}

<label class={["hc_chip"]} class:hc_chip--always-gray={variantGray}>
  {@render input()}
  <span class="hc_chip__handle">
    {@render children?.()}
  </span>
</label>

<style lang="scss">
  .hc_chip {
    --input-color: var(--oc-base-color-gray-25);
    --input-height: var(--oc-base-dimension-relative-32);
    --input-width: 100%;
    --input-border-color: var(--oc-base-color-gray-200);
    --input-border-radius: calc(var(--input-height) / 2);
    --thumb-color: var(--oc-base-color-white);
    --thumb-size: var(--oc-base-dimension-relative-24);
    --checkmark-size: var(--oc-base-dimension-relative-16);
    --checkmark-color-selected: var(--oc-semantic-color-text-default);
    --grid-columns: [input-start] 10px [thumb-start] var(--label-width, max-content) [thumb-end] 10px [input-end];
    --checked-color: var(--oc-base-color-interactive-blue-50);
    --checked-hover-color: var(--oc-base-color-interactive-blue-lighten-35);
    --checked-font-weight: bold;

    align-items: center;
    cursor: pointer;
    grid-template-columns: var(--grid-columns);
    justify-items: center;

    &--always-gray {
      --input-color: var(--oc-base-color-gray-25);
      --checked-color: var(--oc-base-color-gray-25);
      --checked-hover-color: var(--oc-base-color-black-lighten-71);
      --checked-font-weight: normal;
    }

    &:not([hidden]) {
      display: grid;
    }

    &__handle {
      grid-column: thumb-start / thumb-end;
      grid-row: 1;
      display: flex;

      align-items: center;
      justify-content: center;
      width: 100%;
      border-radius: var(--input-border-radius);

      /*                               */
      @at-root :global(input):checked ~ & :global(.hc_label) {
        font-weight: var(--checked-font-weight);
      }

      @at-root :global(input):checked ~ & :global(.hc_color .hc_color__title) {
        font-weight: var(--checked-font-weight);
      }
    }

    :global(input) {
      appearance: none;
      cursor: pointer;
      margin: 0;
      height: var(--input-height);
      width: var(--input-width);
      min-width: 59px;
      border-radius: var(--input-border-radius);
      grid-column: input-start / input-end;
      grid-row: 1;

      &:not(:checked) {
        background-color: var(--input-color);
      }

      &:checked {
        background-color: var(--checked-color);
      }

      &:focus-visible {
        outline: var(--oc-semantic-focus-outline-color, #008cf8) var(--oc-semantic-focus-outline-style, solid)
          var(--oc-semantic-focus-outline-width, 2px);
        outline-offset: var(--oc-semantic-focus-outline-offset, 2px);
      }
    }

    @media (hover: hover) {
      @at-root &:hover :global(input) {
        &:not(:checked) {
          background-color: var(--oc-base-color-black-lighten-71);
        }

        &:checked {
          background-color: var(--checked-hover-color);
        }
      }
      @at-root &:hover &__handle {
        outline-width: var(--oc-semantic-focus-outline-offset, 2px);
      }
    }
  }
</style>
